import SEO from '../components/SEO';
import { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Car, Bike, Check, ArrowRight, User, Phone, Mail, MapPin, FileText, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Navbar } from '../components/Navbar';
import AddressAutocomplete from '../components/AddressAutocomplete';

function DriverApplicationPage() {
  const { t } = useTranslation();
  const { api } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [checkingResume, setCheckingResume] = useState(false);
  const [applicationId, setApplicationId] = useState(null);
  const [assessmentQuestions, setAssessmentQuestions] = useState([]);
  const [answers, setAnswers] = useState([]);
  const [assessmentResult, setAssessmentResult] = useState(null);
  const [resumeInfo, setResumeInfo] = useState(null);
  
  // Check for resume parameter on page load
  useEffect(() => {
    const resumeId = searchParams.get('resume');
    if (resumeId) {
      checkResumeApplication(resumeId);
    }
  }, [searchParams]);

  const checkResumeApplication = async (resumeId) => {
    setCheckingResume(true);
    try {
      const response = await api.get(`/applications/driver/resume/${resumeId}`);
      const data = response.data;
      
      if (data.can_resume) {
        // Load assessment questions and move to step 2
        setApplicationId(resumeId);
        setResumeInfo(data);
        
        const questionsRes = await api.get('/applications/assessment/driver');
        setAssessmentQuestions(questionsRes.data.questions);
        setAnswers(new Array(questionsRes.data.questions.length).fill(-1));
        
        setStep(2);
        toast.success(`Welcome back, ${data.full_name}! Continue your assessment.`);
      } else {
        // Show message based on status
        setResumeInfo(data);
        if (data.status === 'pending_documents') {
          toast.info('Please upload your documents to continue.');
          // Redirect to document upload page
          window.location.href = `/apply/driver/documents?id=${resumeId}`;
        } else if (data.status === 'pending_review') {
          toast.info('Your documents are being reviewed.');
        } else if (data.status === 'approved') {
          toast.success('Your application has been approved!');
        } else if (data.status === 'rejected') {
          toast.error('Your application was not successful.');
        }
      }
    } catch (error) {
      console.error('Resume check error:', error);
      toast.error('Could not find application. Please start a new application.');
    } finally {
      setCheckingResume(false);
    }
  };
  
  const [formData, setFormData] = useState({
    full_name: '',
    phone: '',
    email: '',
    city: '',
    vehicle_type: '',
    has_delivery_experience: false,
    has_vehicle_insurance: false,
    agreed_independent_contractor: false,
    agreed_own_taxes: false,
    agreed_insurance_responsibility: false,
    agreed_terms_conditions: false
  });

  const requiresLicense = formData.vehicle_type === 'car' || formData.vehicle_type === 'scooter';

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const canSubmit = () => {
    const baseRequired = formData.full_name && formData.phone && formData.email && 
      formData.city && formData.vehicle_type && formData.agreed_independent_contractor &&
      formData.agreed_own_taxes && formData.agreed_terms_conditions;
    
    if (requiresLicense) {
      return baseRequired && formData.agreed_insurance_responsibility;
    }
    return baseRequired;
  };

  const handleSubmitApplication = async () => {
    if (!canSubmit()) {
      toast.error('Please fill in all required fields and agree to all terms');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/applications/driver', formData);
      setApplicationId(response.data.application_id);
      
      const questionsRes = await api.get('/applications/assessment/driver');
      setAssessmentQuestions(questionsRes.data.questions);
      setAnswers(new Array(questionsRes.data.questions.length).fill(-1));
      
      setStep(2);
      toast.success('Application submitted! Check your email for a link to resume later if needed.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit application');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerSelect = (questionIndex, answerIndex) => {
    const newAnswers = [...answers];
    newAnswers[questionIndex] = answerIndex;
    setAnswers(newAnswers);
  };

  const handleSubmitAssessment = async () => {
    if (answers.includes(-1)) {
      toast.error('Please answer all questions');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/applications/assessment/submit', {
        application_id: applicationId,
        answers: answers
      });
      
      setAssessmentResult(response.data);
      setStep(3);
      
      // If assessment passed and needs documents, redirect to document upload
      if (response.data.passed && response.data.next_step === 'upload_documents') {
        toast.success('Assessment passed! Redirecting to document upload...');
        setTimeout(() => {
          window.location.href = response.data.upload_url;
        }, 2000);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit assessment');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEO page="driverApplication" />
      <div>
        <Navbar />
        <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white pt-20 pb-12">
          <div className="max-w-2xl mx-auto px-4">
            {/* Loading state when checking resume */}
            {checkingResume && (
              <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
                <RefreshCw className="w-12 h-12 mx-auto mb-4 text-orange-500 animate-spin" />
                <h2 className="text-xl font-semibold text-slate-800">Loading your application...</h2>
                <p className="text-slate-500 mt-2">Please wait while we retrieve your progress.</p>
              </div>
            )}

            {/* Resume info message when application is already completed */}
            {!checkingResume && resumeInfo && !resumeInfo.can_resume && (
              <div className="bg-white rounded-2xl shadow-lg p-8 text-center mb-8">
                <div className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center ${
                  resumeInfo.status === 'approved' ? 'bg-green-100' : 
                  resumeInfo.status === 'pending_review' ? 'bg-blue-100' : 'bg-red-100'
                }`}>
                  {resumeInfo.status === 'approved' ? <Check className="w-8 h-8 text-green-500" /> :
                   resumeInfo.status === 'pending_review' ? <RefreshCw className="w-8 h-8 text-blue-500" /> :
                   <span className="text-2xl">❌</span>}
                </div>
                <h2 className="text-xl font-semibold text-slate-800 mb-2">
                  {resumeInfo.status === 'approved' ? 'Application Approved!' :
                   resumeInfo.status === 'pending_review' ? 'Application Under Review' :
                   'Application Not Successful'}
                </h2>
                <p className="text-slate-500">{resumeInfo.message}</p>
                {resumeInfo.status === 'rejected' && (
                  <Button onClick={() => { setResumeInfo(null); setStep(1); }} className="mt-4 btn-primary">
                    Start New Application
                  </Button>
                )}
              </div>
            )}

            {/* Main application flow */}
            {!checkingResume && (!resumeInfo || resumeInfo.can_resume) && (
              <>
            {/* Progress Steps */}
            <div className="mb-8">
              <div className="flex items-center justify-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${step >= 1 ? 'bg-orange-500 text-white' : 'bg-slate-200 text-slate-500'}`}>
                  {step > 1 ? <Check className="w-5 h-5" /> : '1'}
                </div>
                <div className={`w-20 h-1 mx-2 ${step > 1 ? 'bg-orange-500' : 'bg-slate-200'}`} />
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${step >= 2 ? 'bg-orange-500 text-white' : 'bg-slate-200 text-slate-500'}`}>
                  {step > 2 ? <Check className="w-5 h-5" /> : '2'}
                </div>
              <div className={`w-20 h-1 mx-2 ${step > 2 ? 'bg-orange-500' : 'bg-slate-200'}`} />
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${step >= 3 ? 'bg-orange-500 text-white' : 'bg-slate-200 text-slate-500'}`}>
                3
              </div>
            </div>
            <div className="flex justify-between text-sm text-slate-500 mt-4 px-4">
              <span>{t('driverApp.application')}</span>
              <span>{t('driverApp.assessment')}</span>
              <span>{t('driverApp.result')}</span>
            </div>
          </div>

          {/* Step 1: Application Form */}
          {step === 1 && (
            <div className="bg-white rounded-2xl shadow-lg p-8" data-testid="driver-application-form">
              <h1 className="text-2xl font-bold text-slate-900 mb-2">{t('driverApp.title')}</h1>
              <p className="text-slate-500 mb-6">{t('driverApp.subtitle')}</p>

              <div className="space-y-6">
                <div>
                  <Label className="flex items-center gap-2 mb-2">
                    <User className="w-4 h-4 text-slate-400" />
                    {t('driverApp.fullName')} *
                  </Label>
                  <Input name="full_name" value={formData.full_name} onChange={handleInputChange} placeholder={t('driverApp.enterFullName')} data-testid="driver-name-input" />
                </div>

                <div>
                  <Label className="flex items-center gap-2 mb-2">
                    <Phone className="w-4 h-4 text-slate-400" />
                    {t('driverApp.phoneNumber')} *
                  </Label>
                  <Input name="phone" value={formData.phone} onChange={handleInputChange} placeholder="+44 7XXX XXXXXX" data-testid="driver-phone-input" />
                </div>

                <div>
                  <Label className="flex items-center gap-2 mb-2">
                    <Mail className="w-4 h-4 text-slate-400" />
                    {t('driverApp.email')} *
                  </Label>
                  <Input name="email" type="email" value={formData.email} onChange={handleInputChange} placeholder="your@email.com" data-testid="driver-email-input" />
                </div>

                <div>
                  <Label className="flex items-center gap-2 mb-2">
                    <MapPin className="w-4 h-4 text-slate-400" />
                    {t('driverApp.cityArea')} *
                  </Label>
                  <AddressAutocomplete
                    value={formData.city}
                    onChange={(value) => setFormData(prev => ({ ...prev, city: value }))}
                    onSelect={(address) => setFormData(prev => ({ ...prev, city: address?.address || address?.city || formData.city }))}
                    placeholder={t('driverApp.cityPlaceholder')}
                    data-testid="driver-city-input"
                  />
                </div>

                <div>
                  <Label className="mb-3 block">{t('driverApp.vehicleLabel')}</Label>
                  <div className="grid grid-cols-3 gap-3">
                    <button type="button" onClick={() => setFormData(prev => ({ ...prev, vehicle_type: 'bike' }))} className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${formData.vehicle_type === 'bike' ? 'border-orange-500 bg-orange-50 text-orange-600' : 'border-slate-200 hover:border-slate-300'}`} data-testid="vehicle-bike">
                      <Bike className="w-8 h-8" />
                      <span className="font-medium">{t('driverApp.bicycle')}</span>
                      <span className="text-xs text-slate-500">{t('driverApp.bicycleDesc')}</span>
                    </button>
                    <button type="button" onClick={() => setFormData(prev => ({ ...prev, vehicle_type: 'scooter' }))} className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${formData.vehicle_type === 'scooter' ? 'border-orange-500 bg-orange-50 text-orange-600' : 'border-slate-200 hover:border-slate-300'}`} data-testid="vehicle-scooter">
                      <Bike className="w-8 h-8" />
                      <span className="font-medium">{t('driverApp.scooter')}</span>
                      <span className="text-xs text-slate-500">{t('driverApp.scooterDesc')}</span>
                    </button>
                    <button type="button" onClick={() => setFormData(prev => ({ ...prev, vehicle_type: 'car' }))} className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${formData.vehicle_type === 'car' ? 'border-orange-500 bg-orange-50 text-orange-600' : 'border-slate-200 hover:border-slate-300'}`} data-testid="vehicle-car">
                      <Car className="w-8 h-8" />
                      <span className="font-medium">{t('driverApp.car')}</span>
                      <span className="text-xs text-slate-500">{t('driverApp.carDesc')}</span>
                    </button>
                  </div>
                </div>

                <div className="space-y-3">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input type="checkbox" name="has_delivery_experience" checked={formData.has_delivery_experience} onChange={handleInputChange} className="w-5 h-5 rounded border-slate-300 text-orange-500 focus:ring-orange-500" />
                    <span className="text-slate-700">{t('driverApp.priorExperience')}</span>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input type="checkbox" name="has_vehicle_insurance" checked={formData.has_vehicle_insurance} onChange={handleInputChange} className="w-5 h-5 rounded border-slate-300 text-orange-500 focus:ring-orange-500" />
                    <span className="text-slate-700">{t('driverApp.validInsurance')}</span>
                  </label>
                </div>

                {/* Contractor Agreement Section */}
                <div className="border-t border-slate-200 pt-6 mt-6">
                  <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-orange-500" />
                    {t('driverApp.contractorAgreement')}
                  </h3>
                  <p className="text-sm text-slate-500 mb-4">
                    {t('driverApp.contractorIntro')}
                  </p>
                  
                  <div className="space-y-3 bg-slate-50 rounded-xl p-4">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input type="checkbox" name="agreed_independent_contractor" checked={formData.agreed_independent_contractor} onChange={handleInputChange} className="w-5 h-5 mt-0.5 rounded border-slate-300 text-orange-500 focus:ring-orange-500" />
                      <span className="text-slate-700 text-sm">{t('driverApp.confirmContractor')}</span>
                    </label>
                    
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input type="checkbox" name="agreed_own_taxes" checked={formData.agreed_own_taxes} onChange={handleInputChange} className="w-5 h-5 mt-0.5 rounded border-slate-300 text-orange-500 focus:ring-orange-500" />
                      <span className="text-slate-700 text-sm">{t('driverApp.confirmTaxes')}</span>
                    </label>
                    
                    {requiresLicense && (
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input type="checkbox" name="agreed_insurance_responsibility" checked={formData.agreed_insurance_responsibility} onChange={handleInputChange} className="w-5 h-5 mt-0.5 rounded border-slate-300 text-orange-500 focus:ring-orange-500" />
                        <span className="text-slate-700 text-sm">{t('driverApp.confirmLicense')}</span>
                      </label>
                    )}
                    
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input type="checkbox" name="agreed_terms_conditions" checked={formData.agreed_terms_conditions} onChange={handleInputChange} className="w-5 h-5 mt-0.5 rounded border-slate-300 text-orange-500 focus:ring-orange-500" />
                      <span className="text-slate-700 text-sm">{t('driverApp.confirmTerms').split('Driver Terms')[0]}<Link to="/contractor-terms" target="_blank" className="text-orange-500 underline">{t('driverApp.confirmTerms').includes('Términos') ? 'Términos y Condiciones del Conductor' : 'Driver Terms & Conditions'}</Link>. *</span>
                    </label>
                  </div>
                </div>

                <div className="bg-orange-50 rounded-xl p-4 text-sm text-slate-600">
                  <h4 className="font-semibold text-slate-800 mb-2">{t('driverApp.howPayments')}</h4>
                  <p>{t('driverApp.paymentsDesc')}</p>
                </div>

                <Button onClick={handleSubmitApplication} disabled={loading || !canSubmit()} className="w-full btn-primary py-6" data-testid="submit-application-btn">
                  {loading ? t('checkout.processing') : t('driverApp.continueAssessment')}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
            </div>
          )}

          {/* Step 2: Assessment */}
          {step === 2 && (
            <div className="bg-white rounded-2xl shadow-lg p-8" data-testid="driver-assessment">
              <h1 className="text-2xl font-bold text-slate-900 mb-2">{t('driverApp.assessmentTitle')}</h1>
              <p className="text-slate-500 mb-6">{t('driverApp.passMark')}</p>

              <div className="space-y-6">
                {assessmentQuestions.map((q, qIdx) => (
                  <div key={qIdx} className="p-4 border border-slate-200 rounded-xl">
                    <p className="font-medium text-slate-800 mb-3">{qIdx + 1}. {q.question}</p>
                    <div className="space-y-2">
                      {q.options.map((opt, oIdx) => (
                        <button key={oIdx} type="button" onClick={() => handleAnswerSelect(qIdx, oIdx)} className={`w-full text-left p-3 rounded-lg border transition-all ${answers[qIdx] === oIdx ? 'border-orange-500 bg-orange-50 text-orange-700' : 'border-slate-200 hover:border-slate-300'}`}>
                          {String.fromCharCode(65 + oIdx)}. {opt}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
                <Button onClick={handleSubmitAssessment} disabled={loading || answers.includes(-1)} className="w-full btn-primary py-6">
                  {loading ? t('checkout.processing') : t('driverApp.submitAssessment')}
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Result */}
          {step === 3 && assessmentResult && (
            <div className="bg-white rounded-2xl shadow-lg p-8 text-center" data-testid="assessment-result">
              {assessmentResult.passed ? (
                <>
                  <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-100 flex items-center justify-center">
                    <Check className="w-10 h-10 text-green-500" />
                  </div>
                  <h1 className="text-2xl font-bold text-slate-900 mb-2">{t('driverApp.passed')}</h1>
                  <p className="text-slate-500 mb-4">{t('driverApp.score')}: {assessmentResult.score}/{assessmentResult.total}</p>
                  <p className="text-slate-600 mb-8">{assessmentResult.message}</p>
                  <div className="bg-orange-50 rounded-xl p-6 text-left">
                    <h3 className="font-semibold text-slate-800 mb-2">{t('driverApp.whatNext')}</h3>
                    <ul className="space-y-2 text-slate-600 text-sm">
                      <li>{t('driverApp.nextStep1')}</li>
                      <li>{t('driverApp.nextStep2')}</li>
                      <li>{t('driverApp.nextStep3')}</li>
                      <li>{t('driverApp.nextStep4')}</li>
                    </ul>
                  </div>
                </>
              ) : (
                <>
                  <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-100 flex items-center justify-center text-4xl">😔</div>
                  <h1 className="text-2xl font-bold text-slate-900 mb-2">{t('driverApp.notPassed')}</h1>
                  <p className="text-slate-500 mb-4">{t('driverApp.score')}: {assessmentResult.score}/{assessmentResult.total}</p>
                  <p className="text-slate-600 mb-8">{assessmentResult.message}</p>
                  <Button onClick={() => navigate('/')} variant="outline">{t('driverApp.returnHome')}</Button>
                </>
              )}
            </div>
          )}
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default DriverApplicationPage;
